// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview ID generation utilities for all collections
 * @module @epicdm/db-collections/utils/id
 */

import { nanoid } from 'nanoid'

// Fallback ID generator for environments without crypto.getRandomValues (SSR)
const fallbackId = (size: number = 10): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-'
  let id = ''
  for (let i = 0; i < size; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return id
}

// Safe nanoid that falls back when crypto is unavailable
export const safeNanoid = (size: number = 10): string => {
  try {
    // Check if crypto.getRandomValues is available
    if (
      typeof globalThis !== 'undefined' &&
      globalThis.crypto &&
      typeof globalThis.crypto.getRandomValues === 'function'
    ) {
      return nanoid(size)
    }
    return fallbackId(size)
  } catch {
    return fallbackId(size)
  }
}

/**
 * Canonical mapping from entity type to ID prefix.
 * This is the source of truth for all ID prefixes.
 * Each prefix must be unique and exactly 4 characters long.
 *
 * When adding a new entity type:
 * 1. Add it here with a unique prefix
 * 2. Also update ID_PREFIX_TO_COLLECTION to include the new prefix
 * 3. Use createId('entitytype') to generate IDs
 */
export const ENTITY_TO_PREFIX: Record<string, string> = {
  // Financial entities (singular and plural forms)
  finaccount: 'fina',
  finaccounts: 'fina',
  finbudget: 'finb',
  finbudgets: 'finb',
  fincategory: 'finc',
  fincategories: 'finc',
  finscheduledtransaction: 'fins',
  finscheduledtransactions: 'fins',
  fintransaction: 'fint',
  fintransactions: 'fint',
  finincomestatement: 'fini',
  finincomestatements: 'fini',

  // Business accounting entities (singular and plural forms)
  chartofaccount: 'choa',
  chartofaccounts: 'choa',
  journalentry: 'jrnl',
  journalentries: 'jrnl',
  journalline: 'jrln',
  journallines: 'jrln',
  fiscalperiod: 'fisc',
  fiscalperiods: 'fisc',
  vendor: 'vend',
  vendors: 'vend',
  vendorbill: 'vbil',
  vendorbills: 'vbil',
  bankreconciliation: 'brcn',
  bankreconciliations: 'brcn',
  taxrate: 'taxr',
  taxrates: 'taxr',

  // Core entities
  approval: 'appr',
  approvals: 'appr',
  attribute: 'attr',
  attributes: 'attr',
  client: 'clie',
  clients: 'clie',
  codebase: 'code',
  codebases: 'code',
  contact: 'cont',
  contacts: 'cont',
  conversation: 'conv',
  conversations: 'conv',
  discussion: 'disc',
  discussions: 'disc',
  document: 'docu',
  documents: 'docu',
  flow: 'flow',
  flows: 'flow',
  goal: 'goal',
  goals: 'goal',
  message: 'mess',
  messages: 'mess',
  milestone: 'mile',
  milestones: 'mile',
  opportunity: 'oppo',
  opportunities: 'oppo',
  org: 'org_',
  orgs: 'org_',
  project: 'proj',
  projects: 'proj',
  session: 'sess',
  sessions: 'sess',
  task: 'task',
  tasks: 'task',
  template: 'temp',
  templates: 'temp',
  timeentry: 'time',
  timeentries: 'time',
  user: 'use_',
  users: 'use_',
  variables: 'vari',
  workspace: 'work',
  workspaces: 'work',
  syncconfig: 'sync',
  syncconfigs: 'sync',

  // Product entities (singular and plural forms)
  product: 'prod',
  products: 'prod',
  initiative: 'init',
  initiatives: 'init',
  roadmap: 'road',
  roadmaps: 'road',
  productgoal: 'pgoa',
  productgoals: 'pgoa',
  productproject: 'ppro',
  productprojects: 'ppro',
  productteammember: 'ptmb',
  productteammembers: 'ptmb',
  activity: 'acti',
  activities: 'acti',
  teammember: 'team',
  teammembers: 'team',
  metric: 'metr',
  metrics: 'metr',

  // Process entities (singular and plural forms)
  process: 'proc',
  processes: 'proc',
  processstep: 'step',
  processsteps: 'step',
  processexecution: 'exec',
  processexecutions: 'exec',
  steptemplate: 'stpl',
  steptemplates: 'stpl',

  // Theme entities (singular and plural forms)
  orgtheme: 'othr',
  orgthemes: 'othr',

  // Business Plan entities (singular and plural forms)
  businessplan: 'bpln',
  businessplans: 'bpln',
  marketanalysis: 'mkan',
  marketanalyses: 'mkan',
  competitor: 'comp',
  competitors: 'comp',
  businessplanteammember: 'bptm',
  businessplanteammembers: 'bptm',

  // Marketing entities (singular and plural forms)
  offer: 'offr',
  offers: 'offr',
  campaign: 'camp',
  campaigns: 'camp',
  audience: 'audc',
  audiences: 'audc',
  leadmagnet: 'lead',
  leadmagnets: 'lead',
  campaigncontact: 'ccnt',
  campaigncontacts: 'ccnt',
  campaignproduct: 'cprd',
  campaignproducts: 'cprd',

  // Sales entities (singular and plural forms)
  deal: 'deal',
  deals: 'deal',
  pipeline: 'pipe',
  pipelines: 'pipe',
  pipelinestage: 'stge',
  pipelinestages: 'stge',
  quote: 'quot',
  quotes: 'quot',
  invoice: 'invc',
  invoices: 'invc',
  lineitem: 'item',
  lineitems: 'item',
  dealproduct: 'dprd',
  dealproducts: 'dprd',
  dealproject: 'dpro',
  dealprojects: 'dpro',

  // BizDev entities (singular and plural forms)
  partnership: 'part',
  partnerships: 'part',

  // Recurrence entities
  recurrence: 'recu',
  recurrences: 'recu',

  // Process sub-entities
  edge: 'edge',
  edges: 'edge',
  comment: 'cmnt',
  comments: 'cmnt',

  // Cashflow grouping entities
  categorygroup: 'cgrp',
  categorygroups: 'cgrp',

  // Chat entities
  attachment: 'atch',
  attachments: 'atch',

  // Schema-in-DB entities
  schema: 'schm',
  schemas: 'schm',
  record: 'rec_',
  records: 'rec_',
  relation: 'reln',
  relations: 'reln',

  // Vault entities (singular and plural forms)
  vault: 'vaut',
  vaults: 'vaut',
  vaultitem: 'vait',
  vaultitems: 'vait',
  servicekey: 'skey',
  servicekeys: 'skey',

  // Git connection entities (Phase 3 — provider-agnostic auth handles)
  git_connection: 'gitc',
  git_connections: 'gitc',

  // Git VCA entities (Phase 5 — provider-agnostic records-table collections)
  // Note: 'comments' uses 'gcm_' (git comment) to avoid collision with existing 'cmnt' (generic comment)
  // Note: 'commits' uses 'cmt_' — chosen before 'gcm_' was assigned to comments
  pullrequest: 'prq_',
  pullrequests: 'prq_',
  commit: 'cmt_',
  commits: 'cmt_',
  branch: 'brn_',
  branches: 'brn_',
  release: 'rel_',
  releases: 'rel_',
  workflow_run: 'wfr_',
  workflow_runs: 'wfr_',
  git_comment: 'gcm_',
  git_comments: 'gcm_',
  review: 'grv_',
  reviews: 'grv_',
  review_comment: 'grc_',
  review_comments: 'grc_',
  git_project_link: 'gpl_',
  git_project_links: 'gpl_',
  connector_webhook_event: 'cwe_',
  connector_webhook_events: 'cwe_',

  // Worker entities (singular and plural forms)
  // Note: 'work' prefix is taken by workspaces, so workers use 'wrkr'
  worker: 'wrkr',
  workers: 'wrkr',

  // App snapshot entities (singular and plural forms)
  appsnapshot: 'asnp',
  appsnapshots: 'asnp',

  // RBAC entities (singular and plural forms)
  // Note: 'acl' prefix uses 'acl_' (4 chars with trailing underscore) to stay 4 chars
  role: 'role',
  roles: 'role',
  rolemembership: 'rmem',
  rolememberships: 'rmem',
  acl: 'acl_',
  acls: 'acl_',
  revokedtoken: 'rtok',
  revokedtokens: 'rtok',
  revocationepoch: 'repc',
  revocationepochs: 'repc',
  invitation: 'invt',
  invitations: 'invt',

  // ZK encryption entities
  encryptionkey: 'ekey',
  encryptionkeys: 'ekey',
  userkey: 'ukey',
  userkeys: 'ukey',
  vaultmembership: 'vmem',
  vaultmemberships: 'vmem',

  // Discord entities (singular and plural forms)
  discord_mapping: 'dmap',
  discord_mappings: 'dmap',
  discord_channel: 'dchn',
  discord_channels: 'dchn',

  // Entity assignment junction
  entityassignment: 'easn',
  entityassignments: 'easn',

  // Orchestration entities (singular and plural forms)
  proposal: 'prps',
  proposals: 'prps',
  mission: 'misn',
  missions: 'misn',
  mission_step: 'mstp',
  mission_steps: 'mstp',
  agent_event: 'aevt',
  agent_events: 'aevt',
  trigger_rule: 'trul',
  trigger_rules: 'trul',
  ops_policy: 'opol',
  agent_affinity: 'aaff',
  agent_affinities: 'aaff',

  // Finance VCA aliases (short names used by virtual collection seeds)
  account: 'fina',
  accounts: 'fina',
  budget: 'finb',
  budgets: 'finb',
  transaction: 'fint',
  transactions: 'fint',
  scheduledtransaction: 'fins',
  scheduledtransactions: 'fins',
  incomestatement: 'fini',
  incomestatements: 'fini',

  // Sales entities (additional)
  paymentplan: 'ppln',
  paymentplans: 'ppln',

  // Support entities (singular and plural forms)
  ticket: 'tikt',
  tickets: 'tikt',
  feedbackitem: 'fbki',
  feedbackitems: 'fbki',
  kbarticle: 'kbar',
  kbarticles: 'kbar',
  healthscore: 'hlsc',
  healthscores: 'hlsc',
  integration: 'intg',
  integrations: 'intg',

  // Content entities (singular and plural forms)
  contentpiece: 'cpce',
  contentpieces: 'cpce',
  contentpillar: 'cpil',
  contentpillars: 'cpil',
  hooktemplate: 'htpl',
  hooktemplates: 'htpl',
  conversioncode: 'cvcn',
  conversioncodes: 'cvcn',
  contentfunnel: 'cfun',
  contentfunnels: 'cfun',
  persuasiontemplate: 'prtl',
  persuasiontemplates: 'prtl',
  outreachtarget: 'otrg',
  outreachtargets: 'otrg',

  // Accounting entities (additional)
  budgetitem: 'bdit',
  budgetitems: 'bdit',

  // Logging entities (singular and plural forms)
  // Note: 'log' is only 3 chars, so prefix uses trailing underscore like 'org_', 'rec_', 'use_'
  log: 'log_',
  logs: 'log_',
}

/**
 * Generate a unique ID for an entity type.
 * Format: {prefix}_{nanoid} where prefix is looked up from ENTITY_TO_PREFIX.
 *
 * @param entityType - The entity type (e.g., 'task', 'project', 'goal')
 * @returns A unique ID string (e.g., 'task_abc123xyz0')
 * @throws Error if entityType is not registered in ENTITY_TO_PREFIX
 *
 * @example
 * createId('task')     // 'task_abc123xyz0'
 * createId('project')  // 'proj_xyz789abc1'
 * createId('goal')     // 'goal_def456ghi2'
 */
export const createId = (entityType: string): string => {
  const prefix = ENTITY_TO_PREFIX[entityType]
  if (!prefix) {
    throw new Error(
      `Unknown entity type: "${entityType}". Add it to ENTITY_TO_PREFIX in db-collections/src/utils/id.ts`
    )
  }
  return `${prefix}_${safeNanoid(10)}`
}

/**
 * Map of ID prefixes to collection names.
 * Manually maintained; must be kept in sync with ENTITY_TO_PREFIX.
 * Used to determine the entity type from an ID string.
 */
export const ID_PREFIX_TO_COLLECTION: Record<string, string> = {
  // Manually maintained mapping from ID prefix to collection name (keep in sync with ENTITY_TO_PREFIX)
  fina: 'finaccounts',
  finb: 'finbudgets',
  finc: 'fincategories',
  fins: 'finscheduledtransactions',
  fint: 'fintransactions',
  fini: 'finincomestatements',
  choa: 'chartofaccounts',
  jrnl: 'journalentries',
  jrln: 'journallines',
  fisc: 'fiscalperiods',
  vend: 'vendors',
  vbil: 'vendorbills',
  brcn: 'bankreconciliations',
  taxr: 'taxrates',
  appr: 'approvals',
  attr: 'attributes',
  clie: 'clients',
  code: 'codebases',
  cont: 'contacts',
  conv: 'conversations',
  disc: 'discussions',
  docu: 'documents',
  flow: 'flows',
  goal: 'goals',
  mess: 'messages',
  mile: 'milestones',
  oppo: 'opportunities',
  org_: 'orgs',
  proj: 'projects',
  sess: 'sessions',
  task: 'tasks',
  temp: 'templates',
  time: 'timeentries',
  use_: 'users',
  vari: 'variables',
  work: 'workspaces',
  sync: 'syncconfigs',
  prod: 'products',
  init: 'initiatives',
  road: 'roadmaps',
  pgoa: 'productgoals',
  ppro: 'productprojects',
  ptmb: 'productteammembers',
  acti: 'activities',
  team: 'teammembers',
  metr: 'metrics',
  proc: 'processes',
  step: 'processsteps',
  exec: 'processexecutions',
  stpl: 'steptemplates',
  othr: 'orgthemes',
  bpln: 'businessplans',
  mkan: 'marketanalyses',
  comp: 'competitors',
  bptm: 'businessplanteammembers',
  offr: 'offers',
  camp: 'campaigns',
  audc: 'audiences',
  lead: 'leadmagnets',
  ccnt: 'campaigncontacts',
  cprd: 'campaignproducts',
  deal: 'deals',
  pipe: 'pipelines',
  stge: 'pipelinestages',
  quot: 'quotes',
  invc: 'invoices',
  item: 'lineitems',
  dprd: 'dealproducts',
  dpro: 'dealprojects',
  part: 'partnerships',
  recu: 'recurrences',
  edge: 'edges',
  cmnt: 'comments',
  cgrp: 'categorygroups',
  atch: 'attachments',
  schm: 'schemas',
  rec_: 'records',
  reln: 'relations',
  vaut: 'vaults',
  vait: 'vaultitems',
  skey: 'servicekeys',
  gitc: 'git_connections',
  // Git VCA entities (Phase 5)
  prq_: 'pullrequests',
  cmt_: 'commits',
  brn_: 'branches',
  rel_: 'releases',
  wfr_: 'workflow_runs',
  gcm_: 'git_comments',
  grv_: 'reviews',
  grc_: 'review_comments',
  gpl_: 'git_project_links',
  cwe_: 'connector_webhook_events',
  wrkr: 'workers',
  asnp: 'appsnapshots',
  // RBAC entities
  role: 'roles',
  rmem: 'rolememberships',
  acl_: 'acl',
  rtok: 'revokedtokens',
  repc: 'revocationepochs',
  invt: 'invitations',
  // ZK encryption entities
  ekey: 'encryptionkeys',
  ukey: 'userkeys',
  vmem: 'vaultmemberships',
  // Discord entities
  dmap: 'discord_mappings',
  dchn: 'discord_channels',
  // Entity assignment junction
  easn: 'entityassignments',
  // Orchestration entities
  prps: 'proposals',
  misn: 'missions',
  mstp: 'mission_steps',
  aevt: 'agent_events',
  trul: 'trigger_rules',
  opol: 'ops_policy',
  aaff: 'agent_affinities',
  // Sales entities (additional)
  ppln: 'paymentplans',
  // Support entities
  tikt: 'tickets',
  fbki: 'feedbackitems',
  kbar: 'kbarticles',
  hlsc: 'healthscores',
  intg: 'integrations',
  // Content entities
  cpce: 'contentpieces',
  cpil: 'contentpillars',
  htpl: 'hooktemplates',
  cvcn: 'conversioncodes',
  cfun: 'contentfunnels',
  prtl: 'persuasiontemplates',
  otrg: 'outreachtargets',
  // Accounting entities (additional)
  bdit: 'budgetitems',
  // Logging entities
  // Note: 'log' is 3 chars, so prefix uses trailing underscore (same pattern as org_, rec_, use_)
  log_: 'logs',
}

/**
 * Legacy 3-character prefix mapping for backward compatibility.
 * The mobile app's createId used name.substring(0, 3) instead of 4-char prefixes,
 * producing IDs like 'tas_xxx' instead of 'task_xxx'. This map allows the system
 * to recognize those legacy IDs. Format: 3-char prefix + '_' = 4-char lookup key.
 */
const LEGACY_3CHAR_PREFIX_TO_COLLECTION: Record<string, string> = {
  tas_: 'tasks',
  pro_: 'projects',
  mil_: 'milestones',
  goa_: 'goals',
  dis_: 'discussions',
  doc_: 'documents',
  wor_: 'workspaces',
  org_: 'orgs',
  use_: 'users',
  flo_: 'flows',
  mes_: 'messages',
  ses_: 'sessions',
  tem_: 'templates',
  tim_: 'timeentries',
  cli_: 'clients',
  con_: 'contacts',
  opp_: 'opportunities',
  att_: 'attributes',
  app_: 'approvals',
  var_: 'variables',
  cod_: 'codebases',
}

/**
 * Get the collection name from an entity ID by parsing its prefix.
 * IDs are generated with format: `{prefix}_{nanoid}` where prefix is from ENTITY_TO_PREFIX.
 *
 * @param id - The entity ID (e.g., 'task_abc123', 'proj_xyz789', 'mile_def456')
 * @returns The collection name (e.g., 'tasks', 'projects', 'milestones') or null if unknown
 *
 * @example
 * getCollectionFromId('task_abc123') // returns 'tasks'
 * getCollectionFromId('proj_xyz789') // returns 'projects'
 * getCollectionFromId('mile_def456') // returns 'milestones'
 * getCollectionFromId('unknown_123') // returns null
 */
export const getCollectionFromId = (id: string): string | null => {
  if (!id || typeof id !== 'string') return null

  const prefix = id.substring(0, 4)
  // Try standard 4-char prefix first, then fall back to legacy 3-char prefix mapping
  return ID_PREFIX_TO_COLLECTION[prefix] || LEGACY_3CHAR_PREFIX_TO_COLLECTION[prefix] || null
}

/**
 * Get the entity type (singular form) from an entity ID.
 * This is useful for display purposes and polymorphic relationships.
 *
 * @param id - The entity ID (e.g., 'task_abc123', 'proj_xyz789')
 * @returns The entity type (e.g., 'task', 'project', 'milestone') or null if unknown
 *
 * @example
 * getEntityTypeFromId('task_abc123') // returns 'task'
 * getEntityTypeFromId('proj_xyz789') // returns 'project'
 * getEntityTypeFromId('mile_def456') // returns 'milestone'
 */
export const getEntityTypeFromId = (id: string): string | null => {
  const collection = getCollectionFromId(id)
  if (!collection) return null

  // Map collection names to singular entity types
  const collectionToEntityType: Record<string, string> = {
    finaccounts: 'finaccount',
    approvals: 'approval',
    attributes: 'attribute',
    finbudgets: 'finbudget',
    fincategories: 'fincategory',
    clients: 'client',
    codebases: 'codebase',
    contacts: 'contact',
    conversations: 'conversation',
    discussions: 'discussion',
    flows: 'flow',
    goals: 'goal',
    logs: 'log',
    messages: 'message',
    milestones: 'milestone',
    opportunities: 'opportunity',
    orgs: 'org',
    products: 'product',
    initiatives: 'initiative',
    projects: 'project',
    roadmaps: 'roadmap',
    finscheduledtransactions: 'finscheduledtransaction',
    sessions: 'session',
    documents: 'document',
    tasks: 'task',
    templates: 'template',
    timeentries: 'timeentry',
    fintransactions: 'fintransaction',
    finincomestatements: 'finincomestatement',
    chartofaccounts: 'chartofaccount',
    journalentries: 'journalentry',
    journallines: 'journalline',
    fiscalperiods: 'fiscalperiod',
    vendors: 'vendor',
    vendorbills: 'vendorbill',
    bankreconciliations: 'bankreconciliation',
    taxrates: 'taxrate',
    users: 'user',
    variables: 'variables',
    workspaces: 'workspace',
    syncconfigs: 'syncconfig',
    processes: 'process',
    processsteps: 'processstep',
    processexecutions: 'processexecution',
    steptemplates: 'steptemplate',
    orgthemes: 'orgtheme',
    productgoals: 'productgoal',
    productprojects: 'productproject',
    productteammembers: 'productteammember',
    activities: 'activity',
    teammembers: 'teammember',
    metrics: 'metric',
    businessplans: 'businessplan',
    marketanalyses: 'marketanalysis',
    competitors: 'competitor',
    businessplanteammembers: 'businessplanteammember',
    offers: 'offer',
    campaigns: 'campaign',
    audiences: 'audience',
    leadmagnets: 'leadmagnet',
    campaigncontacts: 'campaigncontact',
    campaignproducts: 'campaignproduct',
    deals: 'deal',
    pipelines: 'pipeline',
    pipelinestages: 'pipelinestage',
    quotes: 'quote',
    invoices: 'invoice',
    lineitems: 'lineitem',
    dealproducts: 'dealproduct',
    dealprojects: 'dealproject',
    partnerships: 'partnership',
    recurrences: 'recurrence',
    edges: 'edge',
    comments: 'comment',
    categorygroups: 'categorygroup',
    attachments: 'attachment',
    schemas: 'schema',
    records: 'record',
    relations: 'relation',
    vaults: 'vault',
    vaultitems: 'vaultitem',
    servicekeys: 'servicekey',
    git_connections: 'git_connection',
    // Git VCA collections (Phase 5)
    pullrequests: 'pullrequest',
    commits: 'commit',
    branches: 'branch',
    releases: 'release',
    workflow_runs: 'workflow_run',
    git_comments: 'git_comment',
    reviews: 'review',
    review_comments: 'review_comment',
    git_project_links: 'git_project_link',
    connector_webhook_events: 'connector_webhook_event',
    workers: 'worker',
    appsnapshots: 'appsnapshot',
    // RBAC collections
    roles: 'role',
    rolememberships: 'rolemembership',
    acl: 'acl',
    revokedtokens: 'revokedtoken',
    revocationepochs: 'revocationepoch',
    // ZK encryption entities
    encryptionkeys: 'encryptionkey',
    userkeys: 'userkey',
    vaultmemberships: 'vaultmembership',
    // Discord collections
    discord_mappings: 'discord_mapping',
    discord_channels: 'discord_channel',
    // Entity assignment junction
    entityassignments: 'entityassignment',
    // Orchestration collections
    proposals: 'proposal',
    missions: 'mission',
    mission_steps: 'mission_step',
    agent_events: 'agent_event',
    trigger_rules: 'trigger_rule',
    ops_policy: 'ops_policy',
    agent_affinities: 'agent_affinity',
    // Sales collections (additional)
    paymentplans: 'paymentplan',
    // Support collections
    tickets: 'ticket',
    feedbackitems: 'feedbackitem',
    kbarticles: 'kbarticle',
    healthscores: 'healthscore',
    integrations: 'integration',
    // Content collections
    contentpieces: 'contentpiece',
    contentpillars: 'contentpillar',
    hooktemplates: 'hooktemplate',
    conversioncodes: 'conversioncode',
    contentfunnels: 'contentfunnel',
    persuasiontemplates: 'persuasiontemplate',
    outreachtargets: 'outreachtarget',
    // Accounting collections (additional)
    budgetitems: 'budgetitem',
  }

  return collectionToEntityType[collection] || null
}
